import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { FaCheckCircle, FaExclamationTriangle, FaShieldAlt, FaArrowLeft } from "react-icons/fa";
import Background3D from "../components/ui/Background3D";
import ThemeCustomizer from "../components/ui/ThemeCustomizer";
import { useTheme } from "../context/ThemeContext";

const AdminCredentialApproval = () => {
  const { token } = useParams();
  const { isDarkMode } = useTheme();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [message, setMessage] = useState({ content: "", type: "" });

  useEffect(() => {
    const fetchRequest = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/auth/approve-admin-credential/${token}`,
        );
        const contentType = response.headers.get("content-type") || "";
        const data = contentType.includes("application/json")
          ? await response.json()
          : { message: "Unexpected server response." };

        if (!response.ok) {
          throw new Error(data.message || "Unable to load approval request.");
        }

        setRequest(data);
      } catch (error) {
        setMessage({ content: error.message, type: "danger" });
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchRequest();
    else {
      setMessage({ content: "Invalid approval link.", type: "danger" });
      setLoading(false);
    }
  }, [token]);

  const handleApprove = async () => {
    setApproving(true);
    setMessage({ content: "", type: "" });

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/approve-admin-credential/${token}`,
        { method: "POST", headers: { "Content-Type": "application/json" } },
      );
      const contentType = response.headers.get("content-type") || "";
      const data = contentType.includes("application/json")
        ? await response.json()
        : { message: "Unexpected server response." };

      if (!response.ok) {
        throw new Error(data.message || "Approval failed.");
      }

      setMessage({ content: data.message, type: "success" });
      setRequest(null);
    } catch (error) {
      setMessage({ content: error.message, type: "danger" });
    } finally {
      setApproving(false);
    }
  };

  const actionLabel = request?.type === "admin_id_change" ? "Admin ID Change" : "Password Reset";

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 relative overflow-hidden transition-colors duration-300 ${isDarkMode ? "dark bg-dark-950" : "bg-mesh"}`}>
      <Background3D variant="auth" />

      <div className="absolute top-4 right-4 z-20">
        <ThemeCustomizer variant="auth" />
      </div>

      <div className="relative w-full max-w-lg z-10 animate-fade-in-up">
        <div className="glass-strong rounded-3xl shadow-3d overflow-hidden border border-white/40 dark:border-dark-600">
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 p-6 text-white text-center">
            <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
              <FaShieldAlt className="w-8 h-8" />
            </div>
            <h2 className="text-2xl font-bold">Approve Admin Change</h2>
            <p className="text-sm text-white/80 mt-1">
              Authorized administrators only
            </p>
          </div>

          <div className="p-6 md:p-8">
            {loading && (
              <p className="text-center text-gray-600 dark:text-gray-300">Loading approval request...</p>
            )}

            {message.content && (
              <div className={`p-4 rounded-xl mb-4 text-sm font-medium flex items-start gap-3 ${
                message.type === "success"
                  ? "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800"
                  : "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800"
              }`} role="alert">
                {message.type === "success"
                  ? <FaCheckCircle className="w-5 h-5 mt-0.5 shrink-0" />
                  : <FaExclamationTriangle className="w-5 h-5 mt-0.5 shrink-0" />}
                <span>{message.content}</span>
              </div>
            )}

            {!loading && request && (
              <div className="space-y-4">
                <div className="rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-800/50 p-4 text-sm space-y-2">
                  <p><span className="font-semibold">Request Type:</span> {actionLabel}</p>
                  <p><span className="font-semibold">Admin ID:</span> {request.attemptedAdminID}</p>
                  {request.newAdminID && (
                    <p><span className="font-semibold">New Admin ID:</span> {request.newAdminID}</p>
                  )}
                  {request.requestIp && (
                    <p><span className="font-semibold">Requested From IP:</span> {request.requestIp}</p>
                  )}
                  {request.expiresAt && (
                    <p><span className="font-semibold">Expires:</span> {new Date(request.expiresAt).toLocaleString()}</p>
                  )}
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Approving this request will immediately apply the change. Only proceed if you recognize this request.
                </p>

                <button
                  type="button"
                  onClick={handleApprove}
                  disabled={approving}
                  className="btn-premium w-full py-3.5"
                >
                  {approving ? "Approving..." : `Approve ${actionLabel}`}
                </button>
              </div>
            )}

            <div className="text-center mt-6">
              <Link to="/login" className="inline-flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:text-secondary-500 font-medium transition-colors">
                <FaArrowLeft className="w-3.5 h-3.5" />
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCredentialApproval;
